import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  SelectChangeEvent,
  TextFieldProps,
  ClickAwayListenerProps,
  IconButtonProps,
} from "@mui/material";
import { SearchScope } from "../types";
import { useScope } from "~/hooks/useScope";

function isValidScope(value: any): value is SearchScope {
  return Object.values(SearchScope).includes(value);
}

export default function useAppSearch() {
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);

  const defaultScope = SearchScope.Organization;
  const [scope, setScope] = useState<SearchScope>(defaultScope);
  const [query, setQuery] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);

  const { organizationScope, spaceScope, workspaceScope } = useScope();
  const [searchParams] = useSearchParams();
  const orgId = organizationScope?.id || "";
  const spaceId = spaceScope?.id || "";
  const workspaceMrn = workspaceScope?.id || "";
  const urlQuery = searchParams.get("q") || "";

  const params = new URLSearchParams({
    ...(spaceId ? { spaceId } : {}),
    ...(workspaceMrn ? { workspaceId: workspaceMrn } : {}),
  });

  useEffect(() => {
    setScope(
      workspaceMrn
        ? SearchScope.Workspace
        : spaceId
          ? SearchScope.Space
          : orgId
            ? SearchScope.Organization
            : defaultScope,
    );
  }, [orgId, spaceId, workspaceMrn]);

  useEffect(() => {
    if (urlQuery && urlQuery !== query) {
      setQuery(urlQuery);
    }
  }, [urlQuery]);

  useEffect(() => {
    const ref = inputRef.current;
    if (!ref) return;

    if (isFocused) {
      ref.focus();
    } else if (!isFocused) {
      ref.blur();
    }

    return () => {
      setIsFocused(false);
      ref.blur();
    };
  }, [inputRef.current, isFocused]);

  const handleScopeChange = (event: SelectChangeEvent<SearchScope>) => {
    const value = event.target.value;
    if (isValidScope(value)) {
      setScope(value);
    }
  };

  const handleScopeKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleFocus: TextFieldProps["onFocus"] = () => {
    setIsFocused(true);
  };

  const blurInput = () => {
    setIsFocused(false);
  };

  const handleToggleFocus = () => {
    if (isFocused) {
      blurInput();
    } else {
      setIsFocused(true);
    }
  };

  const handleClickAway: ClickAwayListenerProps["onClickAway"] = () => {
    blurInput();
  };

  const handleClear: IconButtonProps["onClick"] = () => {
    if (query.length > 0) {
      setQuery("");
    } else {
      blurInput();
    }
  };

  const handleChange: TextFieldProps["onChange"] = (e) => {
    setQuery(e.target.value);
  };

  const handleKeyDown: TextFieldProps["onKeyDown"] = (e) => {
    if (e.key === "Enter") {
      const href =
        scope === SearchScope.Organization
          ? `/organization/search?organizationId=${orgId}`
          : `/space/search?${params.toString()}`;
      navigate(`${href}&q=${encodeURIComponent(query.trim())}`);
      blurInput();
    }
    if (e.key === "Escape") {
      blurInput();
    }
  };

  return {
    orgId,
    spaceId,
    workspaceMrn,
    scope,
    isFocused,
    inputRef,
    query,
    handle: {
      focus: handleFocus,
      toggleFocus: handleToggleFocus,
      change: handleChange,
      scopeChange: handleScopeChange,
      scopeKeyDown: handleScopeKeyDown,
      keyDown: handleKeyDown,
      clear: handleClear,
      clickAway: handleClickAway,
    },
  };
}
